import { useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { buildEvents, MONTHS_PT, formatTime } from "@/lib/events";

const HIGHLIGHT_CATEGORIES = new Set(["especiais", "mulheres", "infantil"]);

const CATEGORY_LABELS = {
  especiais:  { label: "Especial",  className: "bg-[#D4AF37]/20 text-[#D4AF37]" },
  mulheres:   { label: "Mulheres",  className: "bg-pink-500/20 text-pink-300" },
  infantil:   { label: "Infantil",  className: "bg-green-500/20 text-green-300" },
};

function getUpcomingHighlights(n = 3) {
  return buildEvents()
    .filter((ev) => HIGHLIGHT_CATEGORIES.has(ev.category) && ev.recurring !== "weekly")
    .slice(0, n);
}

export default function UpcomingEvents() {
  const events = useMemo(() => getUpcomingHighlights(3), []);

  if (events.length === 0) return null;

  return (
    <section className="py-20 sm:py-28 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">

        {/* cabeçalho */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <p className="text-xs tracking-widest uppercase text-[#D4AF37] mb-3">
              Agenda
            </p>
            <h2 className="font-serif-display text-3xl sm:text-5xl text-white leading-tight">
              Próximos Eventos
            </h2>
          </div>
          <Link
            to="/eventos"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#D4AF37] transition-colors group flex-shrink-0"
          >
            Ver todos
            <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {events.map((event, i) => {
            const [y, m, d] = event.date.split("-").map(Number);
            const dayNum = d;
            const monthName = MONTHS_PT[m - 1];
            const timeLabel = formatTime(event.time);
            const cat = CATEGORY_LABELS[event.category];

            return (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group relative bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/40 transition-all duration-300 rounded-xl overflow-hidden"
              >
                {/* barra lateral dourada */}
                <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#D4AF37] opacity-60 group-hover:opacity-100 transition-opacity" />

                <div className="p-7 pl-9">
                  {/* data */}
                  <div className="flex items-baseline gap-2 mb-5">
                    <span className="text-5xl font-bold text-[#D4AF37] leading-none">{dayNum}</span>
                    <div className="flex flex-col leading-tight">
                      <span className="text-sm font-medium text-white/80">{monthName}</span>
                      <span className="text-xs text-white/40">{y}</span>
                    </div>
                  </div>

                  {/* badge de categoria */}
                  {cat && (
                    <span className={`inline-block text-[10px] px-2.5 py-1 rounded-full font-medium mb-3 ${cat.className}`}>
                      {cat.label}
                    </span>
                  )}
                  {event.recurring === "monthly" && (
                    <span className="inline-block text-[10px] px-2.5 py-1 rounded-full font-medium mb-3 ml-1.5 bg-white/8 text-white/40 border border-white/10">
                      Mensal
                    </span>
                  )}

                  {/* título */}
                  <h3 className="font-serif-display text-xl text-white mb-3 leading-snug">
                    {event.title}
                  </h3>

                  <p className="text-white/50 text-sm leading-relaxed mb-5 line-clamp-2">
                    {event.description}
                  </p>

                  {/* horário */}
                  {timeLabel && (
                    <div className="flex items-center gap-2 text-xs text-white/40">
                      <Clock size={12} />
                      <span>{timeLabel}</span>
                    </div>
                  )}
                  {!timeLabel && event.endDate && (
                    <div className="flex items-center gap-2 text-xs text-white/40">
                      <Calendar size={12} />
                      <span>
                        {event.date.split("-")[2]} a {event.endDate.split("-")[2]}/{event.endDate.split("-")[1]}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
